/**
 * Admin Tasks view — unified Edge + Background queue inspector.
 *
 * Edge tasks       — Firestore-backed `tasks` collection, driven by the
 *                    desktop app via GraphQL `getTasks` / `ackTask`.
 * Background jobs  — eight BullMQ tier queues (control, ingest,
 *                    process, fetch, triage, scoring, aggregate,
 *                    recommend — all under `QUEUE_PREFIX`) plus the
 *                    legacy drain queue `tasks`, consumed by the
 *                    `worker.ts` dispatcher (videos, scoring, evals, etc.).
 */
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {onAuthStateChanged} from 'firebase/auth';
import {auth} from '../../firebase';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  useGetAdminTasks,
  useGetAllYouTubeProfilesLite,
  useGetTaskDebugInfo,
  useListBullmqJobs,
  useCancelBullmqJob,
  useRemoveBullmqJobs,
  useAdminRerunScoringPipeline,
  useRetryEdgeTaskExtraction,
  useRetryAllFailedEdgeTaskExtractions,
  useGetBullmqJobsByIds,
  useGetBullmqWorkers,
  useDeleteEdgeTask,
  useDeleteEdgeTasksByIds,
  useResetEdgeTask,
  useResetEdgeTasksByIds,
} from '../../api/graphql_failed_tasks';
import {useConfirm, useNotify} from '../ui/ConfirmDialog';
import {
  EdgeQueueTask,
  EdgeQueueTaskType,
  TaskParseError,
  TaskState,
  BullmqJobState,
  BullmqJobEntry,
  AnalysisQueueMessageType,
} from '../../types/generated/graphql';
import {usePageTitle} from '../../hooks/usePageTitle';
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Alert,
  Button,
  Tab,
  Tabs,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded';
import ScienceIcon from '@mui/icons-material/Science';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from '../../theme/planapusTheme';
import {
  humanizeTaskState,
  describeTaskState,
  humanizeTaskFailureStage,
  humanizeTaskFailureReason,
  taskStateColors,
  humanizeBullmqJobState,
  bullmqJobStateColors,
} from './agentLabels';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

/**
 * Inline rendering of a many-megabyte JSON blob (typical for
 * INGEST_FEED / INGEST_HISTORY result payloads) hangs the
 * browser at the layout step — `whiteSpace: pre-wrap` on a
 * `<pre>` is O(n) in the rendered text length AND triggers a
 * full reflow per Collapse open. Above this threshold we skip
 * the inline render entirely; the Download button below still
 * works.
 */
const INLINE_RENDER_THRESHOLD_BYTES = 64 * 1024; // 64 KB

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function downloadJsonBlob(content: string, filename: string): void {
  const blob = new Blob([content], {type: 'application/json'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Schedule revoke for after the click handler completes; some
  // browsers cancel the download if we revoke synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * JSON-blob viewer used for the three places the expanded edge-task
 * row renders potentially-huge content (raw decompressed result,
 * BullMQ job payload, BullMQ return value). Always offers a
 * Download button; inlines the content only under the threshold so
 * a 5MB feed-snapshot doesn't hang the browser when expanded.
 */
function JsonBlobViewer({
  label,
  json,
  filename,
}: {
  label: string;
  json: string;
  filename: string;
}): React.JSX.Element {
  const sizeBytes = useMemo(() => {
    try {
      return new TextEncoder().encode(json).length;
    } catch {
      return json.length;
    }
  }, [json]);
  const tooBig = sizeBytes > INLINE_RENDER_THRESHOLD_BYTES;
  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        <Typography sx={sectionLabelSx}>{label}</Typography>
        <Typography sx={{fontSize: 11, color: pcolors.textMuted, fontFamily: 'monospace'}}>
          {formatBytes(sizeBytes)}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadRoundedIcon sx={{fontSize: 14}} />}
          onClick={() => downloadJsonBlob(json, filename)}
          sx={{textTransform: 'none', fontSize: 12, py: 0.25, px: 1}}
        >
          Download
        </Button>
        <IconButton
          size="small"
          onClick={() => navigator.clipboard.writeText(json)}
          aria-label="Copy"
          title="Copy to clipboard"
        >
          <ContentCopyIcon sx={{fontSize: 14}} />
        </IconButton>
      </Box>
      {tooBig ? (
        <Box sx={{mt: 1, ...cardSx, p: 1.5, bgcolor: pcolors.amber[50]}}>
          <Typography sx={{fontSize: 12, color: pcolors.amber[700]}}>
            {formatBytes(sizeBytes)} — too large to render inline.
            Click Download to save the full JSON; the browser would
            otherwise hang while laying out the text.
          </Typography>
        </Box>
      ) : (
        <Box sx={{mt: 1, ...cardSx, p: 1.5, maxHeight: 320, overflow: 'auto'}}>
          <pre
            style={{
              margin: 0,
              fontSize: 11,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              color: pcolors.textPrimary,
            }}
          >
            {json}
          </pre>
        </Box>
      )}
    </Box>
  );
}

function StatePill({children, bg, color}: {children: React.ReactNode; bg: string; color: string}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: bg,
        color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {children}
    </Box>
  );
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString();
}

function TabHeader({title, subtitle, actions}: {title: string; subtitle: string; actions?: React.ReactNode}) {
  // Stacked layout: title + subtitle on one row, actions on the
  // row below. Side-by-side wrapped the subtitle into a tiny
  // column whenever the action row got wide; stacking frees the
  // description's full width and lets actions wrap naturally.
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2}}>
      <Box>
        <Typography sx={{fontSize: 16, fontWeight: 600, color: pcolors.textPrimary}}>{title}</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>{subtitle}</Typography>
      </Box>
      {actions && (
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>{actions}</Box>
      )}
    </Box>
  );
}

// ------------- Edge row ------------------------------------------------------

/**
 * Color map for failureReason chips on FAILED rows.
 * - Auth issues (signed-out, cookie-expired) — red.
 * - Network / rate-limit — amber.
 * - Parser issues — blue.
 * - Worker bug — red.
 */
function failureReasonColors(reason: string | null | undefined): {bg: string; color: string} {
  switch (reason) {
    case 'SIGNED_OUT':
    case 'COOKIE_EXPIRED':
      return {bg: pcolors.red[50], color: pcolors.red[700]};
    case 'NETWORK':
    case 'YOUTUBE_RATE_LIMIT':
    case 'YOUTUBE_INVALID_RESPONSE':
      return {bg: pcolors.amber[50], color: pcolors.amber[700]};
    case 'PARSER_DRIFT':
    case 'PARSER_REJECTED':
      return {bg: pcolors.blue[50], color: pcolors.blue[700]};
    case 'WORKER_ERROR':
      return {bg: pcolors.red[100], color: pcolors.red[700]};
    default:
      return {bg: pcolors.borderLight, color: pcolors.textSecondary};
  }
}

/**
 * Live progress for a row's linked extraction job. Reads
 * `progressJson` (whatever shape BullMQ workers wrote via
 * `job.updateProgress()`) plus the BullMQ job state to render:
 *   - WAITING / DELAYED → a dashed "queued" bar
 *   - ACTIVE            → indeterminate bar OR a determinate bar
 *                         when `fractionComplete` / `current+total`
 *                         are present
 *   - COMPLETED         → 100% bar, success colour
 *   - FAILED            → 100% bar, error colour
 *   - PAUSED            → muted bar
 * Falls back to "—" when no extraction job is linked.
 */
function ExtractionProgressCell({
  job,
  hasJobId,
}: {
  job: BullmqJobEntry | null | undefined;
  hasJobId: boolean;
}) {
  if (!hasJobId) {
    return <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>—</Typography>;
  }
  if (!job) {
    return (
      <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>
        loading…
      </Typography>
    );
  }
  // Try to read a 0..1 fraction out of `progressJson`. Workers
  // typically write `{stage, current, total, fractionComplete, message}`.
  let fraction: number | null = null;
  let stage: string | undefined;
  let message: string | undefined;
  if (job.progressJson) {
    try {
      const p = JSON.parse(job.progressJson) as {
        fractionComplete?: number;
        current?: number;
        total?: number;
        stage?: string;
        message?: string;
      };
      stage = p.stage;
      message = p.message;
      if (typeof p.fractionComplete === 'number') {
        fraction = Math.max(0, Math.min(1, p.fractionComplete));
      } else if (typeof p.current === 'number' && typeof p.total === 'number' && p.total > 0) {
        fraction = Math.max(0, Math.min(1, p.current / p.total));
      }
    } catch {
      // ignore — leave fraction null
    }
  }

  const state = job.state as BullmqJobState;
  let variant: 'determinate' | 'indeterminate' = 'determinate';
  let value = 0;
  let barColor: 'primary' | 'success' | 'error' | 'warning' | 'inherit' = 'primary';
  let label: string | null = null;

  if (state === BullmqJobState.Completed) {
    variant = 'determinate';
    value = 100;
    barColor = 'success';
    label = '100%';
  } else if (state === BullmqJobState.Failed) {
    variant = 'determinate';
    value = 100;
    barColor = 'error';
    label = 'failed';
  } else if (state === BullmqJobState.Active) {
    if (fraction != null) {
      variant = 'determinate';
      value = Math.round(fraction * 100);
      label = `${value}%`;
    } else {
      variant = 'indeterminate';
      label = 'running';
    }
  } else if (state === BullmqJobState.Waiting || state === BullmqJobState.Delayed) {
    variant = 'determinate';
    value = 0;
    barColor = 'inherit';
    label = state === BullmqJobState.Delayed ? 'delayed' : 'queued';
  } else if (state === BullmqJobState.Paused) {
    variant = 'determinate';
    value = fraction != null ? Math.round(fraction * 100) : 0;
    barColor = 'warning';
    label = 'paused';
  }

  const tooltip = [
    stage && `stage: ${stage}`,
    message,
    job.attempts && `attempts: ${job.attempts}`,
    job.failedReason && `error: ${job.failedReason}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={tooltip || `BullMQ ${state}`}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 100}}>
        <LinearProgress
          variant={variant}
          value={value}
          color={barColor}
          sx={{height: 6, borderRadius: 3}}
        />
        <Typography sx={{fontSize: 10, color: pcolors.textSecondary, fontVariantNumeric: 'tabular-nums'}}>
          {label}
          {stage && variant !== 'indeterminate' ? ` · ${stage}` : ''}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// Initials from a profile name. Splits on whitespace, takes the
// first letter of the first two words. Empty / all-whitespace
// names fall back to a 2-char prefix of the profileId so a freshly
// signed-in profile that hasn't synced its display name still gets
// a stable avatar. Uppercased and ASCII-folded for the avatar
// glyph.
function initialsFor(name: string | null | undefined, profileId: string): string {
  const trimmed = (name ?? '').trim();
  if (trimmed.length > 0) {
    const parts = trimmed.split(/\s+/).filter(p => p.length > 0);
    const a = parts[0]?.[0] ?? '';
    const b = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] ?? '');
    const out = `${a}${b}`.toUpperCase();
    if (out.length > 0) return out;
  }
  return profileId.slice(0, 2).toUpperCase();
}

// Deterministic palette: hash the profileId to one of eight
// brand-adjacent colors. Same profile keeps the same color across
// rows + reloads so the eye can match avatars at a glance even
// before reading the tooltip.
const AVATAR_PALETTE = [
  '#3448F0', // indigo (brand)
  '#2563EB', // blue
  '#0891B2', // cyan-700
  '#059669', // emerald-600
  '#9333EA', // purple
  '#DC2626', // red-600
  '#D97706', // amber-600
  '#0F766E', // teal-700
];

function profileColorFor(profileId: string): string {
  let h = 0;
  for (let i = 0; i < profileId.length; i++) {
    h = (h * 31 + profileId.charCodeAt(i)) & 0xffffffff;
  }
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function EdgeRow({
  task,
  client,
  linkedJob,
  profileName,
  selected,
  onToggleSelected,
}: {
  task: EdgeQueueTask;
  client: Props['client'];
  // Resolved BullMQ job for this row's `extractionJobId`, when a
  // batched parent-level fetch is providing it. `null` when there
  // IS an `extractionJobId` but the job has aged out of Redis (or
  // hasn't loaded yet). `undefined` when the task has no extraction
  // job id at all.
  linkedJob?: BullmqJobEntry | null;
  // Resolved profile name from the parent's id→name lookup. Used
  // to render the initials avatar in the Profile column.
  // `undefined` when the parent's profile-list query is still
  // in flight; `null` when the task has no profileId at all.
  profileName?: string | null;
  // Multi-select state — passed from EdgePanel so the parent owns
  // the selection and can drive the bulk-delete button.
  selected: boolean;
  onToggleSelected: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Anchor for the per-row actions kebab menu. Null = closed.
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const {getTaskDebugInfo, data: debugData, loading: debugLoading} = useGetTaskDebugInfo(client);
  const {bg, color} = taskStateColors(task.state);
  const [retryExtraction, retryState] = useRetryEdgeTaskExtraction(client);
  const [rerunScoring, rerunScoringState] = useAdminRerunScoringPipeline(client);
  const [deleteTask, deleteState] = useDeleteEdgeTask(client);
  const [resetTask, resetState] = useResetEdgeTask(client);
  const confirmDelete = useConfirm();
  const confirmReset = useConfirm();
  const notify = useNotify();

  const handleDelete = async (): Promise<void> => {
    const ok = await confirmDelete({
      title: 'Delete this edge task?',
      message: `Permanently removes task ${task.id.slice(0, 12)}… and any cached payload from storage. Snapshots already extracted from this task are not affected.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteTask({variables: {taskId: task.id}});
      notify({severity: 'success', message: `Task ${task.id.slice(0, 8)}… deleted.`});
    } catch (err) {
      notify({
        severity: 'error',
        message: `Delete failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // Hard-reset handler. Drops the BullMQ row, wipes Firestore
  // claim/extraction metadata, re-installs the task as fresh
  // QUEUED. Recovery for tasks BullMQ exhausted (3 attempts →
  // FAILED) whose Firestore mirror still says QUEUED — those
  // are invisible to claimers without manual intervention.
  // No-op when the task doc is missing or its session can't be
  // resolved; the result.reset boolean tells us which.
  const handleReset = async (): Promise<void> => {
    const ok = await confirmReset({
      title: 'Reset this task to QUEUED?',
      message:
        `Drops the BullMQ row, clears claim + extraction metadata, ` +
        `wipes any cached result blob, and re-installs the task as ` +
        `fresh QUEUED with retryCount 0. Use this for tasks BullMQ ` +
        `exhausted (3 attempts → FAILED) whose Firestore mirror still ` +
        `says QUEUED — they're invisible to claimers without this ` +
        `recovery. Snapshots already extracted from this task are not ` +
        `affected; downstream pipelines won't re-run until the next ` +
        `claimer completes the reset task.`,
      confirmLabel: 'Reset',
    });
    if (!ok) return;
    try {
      const res = await resetTask({variables: {taskId: task.id}});
      const ok = res.data?.resetEdgeTask?.reset === true;
      notify({
        severity: ok ? 'success' : 'warning',
        message: ok
          ? `Task ${task.id.slice(0, 8)}… reset to fresh QUEUED.`
          : `Reset returned false for ${task.id.slice(0, 8)}… — task may be missing or session unresolvable. See server log.`,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const handleExpand = () => {
    if (!open && !debugData) {
      getTaskDebugInfo({variables: {taskId: task.id}});
    }
    setOpen(!open);
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  // Retry extraction → enqueue a BG job. Navigation to the BG-jobs
  // panel is opt-in (the toast offers a "View job" deep link) so the
  // admin can trigger several retries in a row without losing their
  // place in the edge-tasks list.
  const handleRetryExtraction = async (dryRun: boolean) => {
    try {
      const res = await retryExtraction({variables: {taskId: task.id, dryRun}});
      const jobId = res.data?.retryEdgeTaskExtraction?.jobId;
      notify({
        severity: jobId ? 'success' : 'warning',
        message: jobId
          ? `Extraction ${dryRun ? 'dry-run ' : ''}enqueued — job #${jobId}`
          : `Failed to enqueue extraction for task ${task.id.slice(0, 8)}…`,
        action: jobId
          ? {label: 'View job', href: `#/admin/operations/tasks?jobId=${jobId}`}
          : undefined,
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Retry failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // Failure attribution chip — only renders when the task is FAILED
  // and the structured fields are populated (most rows post-migration).
  const failureReasonChip = task.failureReason ? failureReasonColors(task.failureReason) : null;
  // Only show retry buttons for task types that actually go through
  // the versioned-parser fallback chain. Transcribe writes directly
  // to the transcript cache so a retry there has nothing to re-run.
  const isExtractable =
    task.type === EdgeQueueTaskType.QueryFeed ||
    task.type === EdgeQueueTaskType.QueryHistory ||
    task.type === EdgeQueueTaskType.QueryRelated;
  // Re-running the scoring pipeline is only meaningful for feed/history
  // (QUERY_RELATED's downstream is corrective-action invalidation, not
  // scoring) and only when the snapshot has been persisted (COMPLETED).
  const canRerunScoring =
    task.state === TaskState.Completed &&
    (task.type === EdgeQueueTaskType.QueryFeed ||
      task.type === EdgeQueueTaskType.QueryHistory);

  const handleRerunScoring = async () => {
    try {
      const res = await rerunScoring({variables: {sourceTaskId: task.id}});
      const r = res.data?.adminRerunScoringPipeline;
      notify({
        severity: 'success',
        message: r
          ? `Re-enqueued SCORE_${r.source} for task ${task.id.slice(0, 8)}… (${r.videoCount} videos)`
          : `Rerun enqueued for task ${task.id.slice(0, 8)}…`,
        action: {label: 'View jobs', href: `#/admin/operations/tasks?tab=background`},
        durationMs: 8000,
      });
    } catch (err) {
      notify({
        severity: 'error',
        message: `Rerun failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  return (
    <>
      <TableRow
        hover
        selected={selected}
        sx={{
          '& > *': {borderBottom: `1px solid ${pcolors.borderLight}`},
          '&:hover': {bgcolor: pcolors.rowHover},
        }}
      >
        <TableCell sx={{...tableBodyCellSx, width: 36, p: 0.5}}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelected(task.id)}
            inputProps={{'aria-label': `Select task ${task.id}`}}
          />
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, width: 36}}>
          <IconButton size="small" onClick={handleExpand}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          {/* Profile column. Renders an initials avatar instead of
              the truncated id — admins recognize their profiles by
              name, not by uuid prefix. Tooltip carries the full
              name + id, the avatar links to the profile page, and
              the full id is still copyable from the expander. */}
          {task.profileId ? (
            <Tooltip
              title={
                profileName
                  ? `${profileName} — click to open report`
                  : `Profile ${task.profileId} — click to open report`
              }
            >
              <Box
                component="a"
                href={`#/admin/profile/${task.profileId}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: profileColorFor(task.profileId),
                  color: '#FFFFFF',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textDecoration: 'none',
                  '&:hover': {filter: 'brightness(0.92)'},
                }}
              >
                {initialsFor(profileName, task.profileId)}
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="This task has no profile (likely a legacy or partially-ingested row).">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: pcolors.borderLight,
                  color: pcolors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                —
              </Box>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 12}}>{task.type}</TableCell>
        <TableCell sx={tableBodyCellSx}>
          {/* State pill. The failure message is folded into this
              tooltip — it used to be a dedicated Error column, but a
              single 400-char error blew out the table width. The
              full message also still shows in the expander below. */}
          {(() => {
            const errorMsg =
              task.failureMessage || task.result?.errorMessage || '';
            return (
              <Tooltip
                title={
                  <Box sx={{fontSize: 12, whiteSpace: 'pre-wrap'}}>
                    {describeTaskState(task.state)}
                    {errorMsg && (
                      <Box
                        component="span"
                        sx={{display: 'block', mt: 1, color: pcolors.red[500]}}
                      >
                        {errorMsg}
                      </Box>
                    )}
                  </Box>
                }
              >
                <span>
                  <StatePill bg={bg} color={color}>
                    {humanizeTaskState(task.state)}
                  </StatePill>
                </span>
              </Tooltip>
            );
          })()}
        </TableCell>
        {/* Device — class + (when known) the specific device's
            short id. Class comes from `executedBy` (set on ack);
            specific device from `claimedByDeviceId` (set
            atomically with state→FETCHING, so we know who's
            holding an in-flight task too, not just completed ones). */}
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary}}>
          {task.executedBy || task.claimedByDeviceId ? (
            <Tooltip
              title={
                task.claimedByDeviceId
                  ? `Device id: ${task.claimedByDeviceId}${
                      task.executedBy ? ` · class: ${task.executedBy}` : ''
                    }`
                  : task.executedBy === 'unknown'
                    ? 'Acked by a client that did not report its device class (legacy build)'
                    : `Class: ${task.executedBy}`
              }
            >
              <span>
                {task.executedBy ?? 'edge'}
                {task.claimedByDeviceId && (
                  <span style={{opacity: 0.6, fontFamily: 'monospace'}}>
                    {' '}· {task.claimedByDeviceId.slice(0, 6)}
                  </span>
                )}
              </span>
            </Tooltip>
          ) : (
            <span>—</span>
          )}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary}}>
          <Tooltip title={new Date(task.createdAt).toLocaleString()}>
            <span>{formatRelative(task.createdAt)}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary, fontVariantNumeric: 'tabular-nums'}}>
          {task.retryCount}/{task.maxRetries}
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          {/* Failure attribution — single column replacing the old
              Extraction column. Renders the structured failureStage
              + failureReason chips when the task is FAILED, plus the
              parserVersion (which is independent of failure — it
              records which parser produced the persisted snapshot). */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
            {task.failureStage && (
              <Tooltip title={`Failed at the ${task.failureStage} stage`}>
                <span>
                  <StatePill bg={pcolors.borderLight} color={pcolors.textSecondary}>
                    {humanizeTaskFailureStage(task.failureStage)}
                  </StatePill>
                </span>
              </Tooltip>
            )}
            {task.failureReason && failureReasonChip && (
              <Tooltip title={task.failureMessage ?? ''}>
                <span>
                  <StatePill bg={failureReasonChip.bg} color={failureReasonChip.color}>
                    {humanizeTaskFailureReason(task.failureReason)}
                  </StatePill>
                </span>
              </Tooltip>
            )}
            {task.parserVersion && (
              <Typography sx={{fontSize: 11, fontFamily: 'monospace', color: pcolors.textSecondary}}>
                {task.parserVersion}
              </Typography>
            )}
            {!task.failureStage && !task.failureReason && !task.parserVersion && (
              <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>—</Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          {/* Job — the linked BullMQ extraction job. Linkable
              jobId on top, state pill underneath. */}
          {task.extractionJobId ? (
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
              <Tooltip title="View extraction job in Background jobs panel">
                <a
                  href={`#/admin/operations/tasks?jobId=${task.extractionJobId}`}
                  style={{
                    fontSize: 11,
                    color: pcolors.blue[700],
                    textDecoration: 'none',
                    fontFamily: 'monospace',
                  }}
                >
                  #{task.extractionJobId}
                </a>
              </Tooltip>
              {linkedJob && (() => {
                const tone = bullmqJobStateColors(linkedJob.state);
                return (
                  <StatePill bg={tone.bg} color={tone.color}>
                    {humanizeBullmqJobState(linkedJob.state)}
                  </StatePill>
                );
              })()}
              {task.extractionJobId && linkedJob === null && (
                <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
                  not in Redis (aged out)
                </Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>—</Typography>
          )}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, minWidth: 140}}>
          {/* Progress — live BullMQ job progress for the linked
              extraction job. Polls at the panel level (3s) so the
              bar advances without manual refresh. Only meaningful
              while the job is ACTIVE/WAITING; on COMPLETED/FAILED
              we just show the terminal label. */}
          <ExtractionProgressCell job={linkedJob} hasJobId={!!task.extractionJobId} />
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, width: 56, textAlign: 'right'}}>
          {/* Row actions collapsed into a kebab menu so the row
              stays compact. Retry/dry-run are gated on extractable
              types (only feed/history/related go through the parser
              chain); re-run scoring on completed feed/history
              snapshots; reset + delete are always available. */}
          <Tooltip title="Task actions">
            <IconButton
              size="small"
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={{color: pcolors.textSecondary}}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
          >
            {isExtractable && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleRetryExtraction(false);
                }}
                disabled={retryState.loading}
              >
                <ListItemIcon>
                  <ReplayIcon fontSize="small" sx={{color: pcolors.blue[600]}} />
                </ListItemIcon>
                <ListItemText
                  primary="Retry extraction"
                  secondary="Re-run the parser fallback chain on this payload"
                />
              </MenuItem>
            )}
            {isExtractable && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleRetryExtraction(true);
                }}
                disabled={retryState.loading}
              >
                <ListItemIcon>
                  <ScienceIcon fontSize="small" sx={{color: pcolors.textSecondary}} />
                </ListItemIcon>
                <ListItemText
                  primary="Dry-run extraction"
                  secondary="Parse + log per-version attempts; no snapshot persisted"
                />
              </MenuItem>
            )}
            {canRerunScoring && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleRerunScoring();
                }}
                disabled={rerunScoringState.loading}
              >
                <ListItemIcon>
                  <PlayArrowIcon fontSize="small" sx={{color: pcolors.green[600]}} />
                </ListItemIcon>
                <ListItemText
                  primary="Re-run scoring"
                  secondary="Re-enqueue the scoring pipeline for this snapshot's videos"
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                handleReset();
              }}
              disabled={resetState.loading}
            >
              <ListItemIcon>
                <RestartAltIcon fontSize="small" sx={{color: pcolors.amber[700]}} />
              </ListItemIcon>
              <ListItemText
                primary="Reset to QUEUED"
                secondary="Drop BullMQ row, wipe claim state, re-queue fresh"
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                handleDelete();
              }}
              disabled={deleteState.loading}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{color: pcolors.red[600]}} />
              </ListItemIcon>
              <ListItemText
                primary="Delete task"
                secondary="Remove the Firestore doc and any cached blob"
                primaryTypographyProps={{sx: {color: pcolors.red[600]}}}
              />
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        {/* `width: 0` + `maxWidth: 0` is the standard CSS trick to
            make a colspan cell NOT influence the table's column-width
            algorithm. With it the expander's content is constrained
            to the existing layout regardless of what's inside.
            `tableLayout` from the parent table propagates here. */}
        <TableCell sx={{p: 0, borderBottom: 'none', width: 0, maxWidth: 0}} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{p: 2, bgcolor: pcolors.bg, overflow: 'hidden', minWidth: 0}}>
              {/* Task identifiers — secondary now that the row column
                  shows profile id. Both ids are abbreviated in the
                  rendering (full text in tooltip + copy button) so
                  long session-id strings can't push the table wider. */}
              <Box sx={{mb: 2, display: 'grid', gap: 0.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, minWidth: 0}}>
                  <Typography sx={{color: pcolors.textMuted, minWidth: 80, flexShrink: 0}}>Task id</Typography>
                  <Tooltip title={task.id}>
                    <Typography sx={{fontFamily: 'monospace', color: pcolors.textPrimary, cursor: 'help'}}>
                      {task.id}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Copy task id">
                    <IconButton size="small" onClick={() => copyToClipboard(task.id)} sx={{p: 0.25}}>
                      <ContentCopyIcon sx={{fontSize: 12}} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, minWidth: 0}}>
                  <Typography sx={{color: pcolors.textMuted, minWidth: 80, flexShrink: 0}}>Profile id</Typography>
                  <Tooltip title={task.profileId}>
                    <Typography sx={{fontFamily: 'monospace', color: pcolors.textPrimary, cursor: 'help'}}>
                      {task.profileId.slice(0, 20)}…{task.profileId.slice(-6)}
                    </Typography>
                  </Tooltip>
                  <Tooltip title="Copy profile id">
                    <IconButton size="small" onClick={() => copyToClipboard(task.profileId)} sx={{p: 0.25}}>
                      <ContentCopyIcon sx={{fontSize: 12}} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Full error message — wraps so the admin can read the
                  whole thing without copying it out. The row's Error
                  column truncates with an ellipsis + tooltip; this
                  is the canonical place to read the entire message. */}
              {(task.failureMessage || task.result?.errorMessage) && (
                <Box sx={{mb: 2}}>
                  <Typography sx={sectionLabelSx}>Error</Typography>
                  <Box sx={{mt: 1, ...cardSx, p: 1.5, bgcolor: pcolors.red[50]}}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: pcolors.red[700],
                      }}
                    >
                      {task.failureMessage || task.result?.errorMessage}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{p: 0.25, mt: 0.5}}
                      onClick={() =>
                        copyToClipboard(
                          (task.failureMessage || task.result?.errorMessage) ?? '',
                        )
                      }
                    >
                      <ContentCopyIcon sx={{fontSize: 12}} />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {debugLoading ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <CircularProgress size={16} />
                  <Typography sx={{fontSize: 13}}>Loading debug info…</Typography>
                </Box>
              ) : debugData?.getTaskDebugInfo ? (
                <Box sx={{display: 'grid', gap: 2}}>
                  {debugData.getTaskDebugInfo.parseErrors && debugData.getTaskDebugInfo.parseErrors.length > 0 && (
                    <Box>
                      <Typography sx={sectionLabelSx}>Parse errors</Typography>
                      <Box sx={{mt: 1, ...cardSx, p: 1.5}}>
                        {debugData.getTaskDebugInfo.parseErrors.map((e: TaskParseError, i: number) => (
                          <Box key={i} sx={{display: 'flex', gap: 1, py: 0.5, fontSize: 12}}>
                            <Typography sx={{fontFamily: 'monospace', color: pcolors.red[700], minWidth: 140}}>
                              {e.field}
                            </Typography>
                            <Typography sx={{fontSize: 12}}>{e.message}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {debugData.getTaskDebugInfo.rawDataJson && (
                    <JsonBlobViewer
                      label="Raw data"
                      json={debugData.getTaskDebugInfo.rawDataJson}
                      filename={`task-${task.id}-raw.json`}
                    />
                  )}
                  {!debugData.getTaskDebugInfo.parseErrors?.length && !debugData.getTaskDebugInfo.rawDataJson && (
                    <Alert severity="info" variant="outlined" sx={{fontSize: 12}}>No debug information available.</Alert>
                  )}
                </Box>
              ) : (
                <Alert severity="warning" variant="outlined" sx={{fontSize: 12}}>Could not load debug information.</Alert>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ------------- Edge panel ----------------------------------------------------

const ALL_TASK_STATES: TaskState[] = [
  TaskState.Queued,
  TaskState.Fetching,
  TaskState.Extracting,
  TaskState.Completed,
  TaskState.Failed,
  TaskState.Cancelled,
];

const ALL_TASK_TYPES: EdgeQueueTaskType[] = [
  EdgeQueueTaskType.QueryFeed,
  EdgeQueueTaskType.QueryHistory,
  EdgeQueueTaskType.QueryRelated,
  EdgeQueueTaskType.Transcribe,
];

function EdgePanel({client}: {client: Props['client']}) {
  // Default to every state — the table shows all edge tasks and the
  // admin narrows via the States multi-select (same pattern as the
  // Background jobs tab's queue scope). Was `[Failed]` only, which
  // hid the QUEUED/FETCHING tasks an admin needs to see when
  // debugging stuck workers.
  const [states, setStates] = useState<TaskState[]>(ALL_TASK_STATES);
  const [taskTypes, setTaskTypes] = useState<EdgeQueueTaskType[]>([]);
  const [limit, setLimit] = useState(100);

  const {data, loading, refetch} = useGetAdminTasks(client, {states, taskTypes, limit});
  const tasks = data?.getAdminTasks || [];

  // Light-weight profile lookup so each row can render an initials
  // avatar instead of the truncated id. Cache-first — admins page
  // through Edge Tasks rapidly and the profile list is small + stable.
  const {data: profilesData} = useGetAllYouTubeProfilesLite(client);
  const profileNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profilesData?.getAllYouTubeProfiles ?? []) {
      if (p && p.id) m.set(p.id, p.name ?? '');
    }
    return m;
  }, [profilesData]);

  // Batch-fetch the BullMQ jobs linked to visible tasks so each row
  // can show the live extraction-job state + progress without one
  // round-trip per row. Memoize the id list so polling doesn't churn
  // when tasks load in (the underlying useQuery skips when empty).
  const linkedJobIds = useMemo(
    () =>
      Array.from(
        new Set(
          tasks
            .map(t => t.extractionJobId)
            .filter((id): id is string => !!id),
        ),
      ),
    [tasks],
  );
  const {data: linkedJobsData} = useGetBullmqJobsByIds(client, linkedJobIds);
  const linkedJobsById = useMemo(() => {
    const m = new Map<string, BullmqJobEntry>();
    for (const j of linkedJobsData?.getBullmqJobsByIds ?? []) {
      if (j) m.set(j.id, j);
    }
    return m;
  }, [linkedJobsData]);

  const [retryAllFailedExtractions, retryAllState] = useRetryAllFailedEdgeTaskExtractions(client);
  const [retryAllOpen, setRetryAllOpen] = useState(false);
  const [bulkDeleteByIds, bulkDeleteState] = useDeleteEdgeTasksByIds(client);
  const [bulkResetByIds, bulkResetState] = useResetEdgeTasksByIds(client);

  // Multi-select state for the bulk-delete checkbox UI. Stored as
  // a Set of task ids so toggling is O(1). The Set is recreated on
  // each change so React picks up the update.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Drop selections that no longer exist after a refetch (deleted
  // tasks would otherwise stay in the set forever).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visible = new Set(tasks.map(t => t.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of selectedIds) {
      if (visible.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelectedIds(next);
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelected = (taskId: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };
  const toggleSelectAll = (): void => {
    if (selectedIds.size === tasks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tasks.map(t => t.id)));
    }
  };
  const handleBulkDelete = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirmTask({
      title: `Delete ${ids.length} edge task${ids.length === 1 ? '' : 's'}?`,
      message:
        `Permanently removes ${ids.length} task doc${ids.length === 1 ? '' : 's'} ` +
        `and any cached payload blob${ids.length === 1 ? '' : 's'} from storage. ` +
        `Snapshots already extracted from these tasks are NOT affected.`,
      confirmLabel: `Delete ${ids.length}`,
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await bulkDeleteByIds({variables: {taskIds: ids}});
      const n = res.data?.deleteEdgeTasksByIds?.deleted ?? 0;
      notify({
        severity: 'success',
        message: `Deleted ${n} task${n === 1 ? '' : 's'}.`,
      });
      setSelectedIds(new Set());
      refetch();
    } catch (err) {
      notify({
        severity: 'error',
        message: `Bulk delete failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  // Bulk hard-reset across the multi-select. Same recovery semantics
  // as the per-row reset — drops BullMQ rows, wipes claim/extraction
  // metadata, re-installs as fresh QUEUED. Concurrency-bounded
  // server-side. Result count may be less than `ids.length` when
  // some tasks were deleted between selection and submit, or when
  // their session can't be resolved (logged server-side).
  const handleBulkReset = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirmTask({
      title: `Reset ${ids.length} edge task${ids.length === 1 ? '' : 's'} to QUEUED?`,
      message:
        `Drops the BullMQ row${ids.length === 1 ? '' : 's'}, clears ` +
        `claim + extraction metadata, wipes any cached result blob, ` +
        `and re-installs ${ids.length === 1 ? 'the task' : 'each task'} as fresh ` +
        `QUEUED with retryCount 0. Use this for tasks BullMQ exhausted ` +
        `(3 attempts → FAILED) whose Firestore mirror still says QUEUED.`,
      confirmLabel: `Reset ${ids.length}`,
    });
    if (!ok) return;
    try {
      const res = await bulkResetByIds({variables: {taskIds: ids}});
      const n = res.data?.resetEdgeTasksByIds?.reset ?? 0;
      notify({
        severity: n === ids.length ? 'success' : 'warning',
        message:
          n === ids.length
            ? `Reset ${n} task${n === 1 ? '' : 's'} to fresh QUEUED.`
            : `Reset ${n} of ${ids.length} task${ids.length === 1 ? '' : 's'}. ` +
              `Skipped rows had missing docs or unresolvable sessions — see server log.`,
      });
      setSelectedIds(new Set());
      refetch();
    } catch (err) {
      notify({
        severity: 'error',
        message: `Bulk reset failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  const notify = useNotify();
  const confirmTask = useConfirm();

  return (
    <Box>
      <TabHeader
        title="Edge tasks"
        subtitle="Work dispatched to the desktop app. One Firestore row per task; the desktop acks results back via ackTask."
        actions={
          <>
            {/* Single bulk-retry entry point — pick types + time
                window inside the dialog. Replaces the per-type
                buttons (feed / history) that previously lived
                here; the dialog covers both with one click and
                also lets the admin scope retries to a time window. */}
            <Tooltip title="Retry all FAILED extractions across selected types and time window — pick scope in the dialog.">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<ReplayIcon />}
                  onClick={() => setRetryAllOpen(true)}
                  disabled={retryAllState.loading}
                >
                  Retry all failed…
                </Button>
              </span>
            </Tooltip>
            {selectedIds.size > 0 && (
              <>
                <Tooltip title={`Reset the ${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'} selected via the row checkboxes — drops BullMQ rows, wipes claim/extraction state, re-installs as fresh QUEUED.`}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={handleBulkReset}
                      disabled={bulkResetState.loading}
                      startIcon={<RestartAltIcon />}
                    >
                      Reset {selectedIds.size} selected
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={`Delete the ${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'} selected via the row checkboxes.`}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteState.loading}
                      startIcon={<DeleteIcon />}
                    >
                      Delete {selectedIds.size} selected
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          <FormControl size="small" sx={{minWidth: 240}}>
            <InputLabel>States</InputLabel>
            <Select
              multiple
              value={states}
              input={<OutlinedInput label="States" />}
              onChange={e =>
                setStates(
                  typeof e.target.value === 'string'
                    ? (e.target.value.split(',') as TaskState[])
                    : (e.target.value as TaskState[])
                )
              }
              renderValue={sel => {
                const arr = sel as TaskState[];
                // Empty and full selections both return everything
                // (the hook maps an empty array to a null filter);
                // collapse both to "All states" instead of spelling
                // out all six.
                if (arr.length === 0 || arr.length === ALL_TASK_STATES.length)
                  return 'All states';
                return arr.map(humanizeTaskState).join(', ');
              }}
            >
              {ALL_TASK_STATES.map(s => (
                <MenuItem key={s} value={s} sx={{fontSize: 13}}>
                  <Checkbox size="small" checked={states.indexOf(s) > -1} />
                  <ListItemText primary={humanizeTaskState(s)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 240}}>
            <InputLabel>Task types</InputLabel>
            <Select
              multiple
              value={taskTypes}
              input={<OutlinedInput label="Task types" />}
              onChange={e =>
                setTaskTypes(
                  typeof e.target.value === 'string'
                    ? (e.target.value.split(',') as EdgeQueueTaskType[])
                    : (e.target.value as EdgeQueueTaskType[])
                )
              }
              renderValue={sel =>
                (sel as EdgeQueueTaskType[]).length === 0
                  ? 'All types'
                  : (sel as EdgeQueueTaskType[]).join(', ')
              }
            >
              {ALL_TASK_TYPES.map(t => (
                <MenuItem key={t} value={t} sx={{fontSize: 13, fontFamily: 'monospace'}}>
                  <Checkbox size="small" checked={taskTypes.indexOf(t) > -1} />
                  <ListItemText primary={t} primaryTypographyProps={{fontFamily: 'monospace', fontSize: 13}} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 100}}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit.toString()} label="Limit" onChange={e => setLimit(Number(e.target.value))}>
              {[25, 50, 100, 250].map(n => (
                <MenuItem key={n} value={n} sx={{fontSize: 13}}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{flex: 1}} />
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
            {tasks.length} task{tasks.length === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{...cardSx, overflow: 'hidden'}}>
        {loading && !data ? (
          <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}><CircularProgress size={24} /></Box>
        ) : tasks.length === 0 ? (
          <Box sx={{p: 5, textAlign: 'center'}}>
            <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>No edge tasks match the selected filters.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={{...tableHeadCellSx, width: 36, p: 0.5}}>
                    {/* "Select all visible" — indeterminate when
                        a partial selection exists, checked when
                        every row is selected. */}
                    <Checkbox
                      size="small"
                      checked={tasks.length > 0 && selectedIds.size === tasks.length}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < tasks.length}
                      onChange={toggleSelectAll}
                      inputProps={{'aria-label': 'Select all tasks'}}
                    />
                  </TableCell>
                  <TableCell sx={tableHeadCellSx} />
                  <TableCell sx={tableHeadCellSx}>Profile</TableCell>
                  <TableCell sx={tableHeadCellSx}>Type</TableCell>
                  <TableCell sx={tableHeadCellSx}>State</TableCell>
                  {/* Device that completed the task. Today this is
                      the legacy `executedBy` string ('desktop' /
                      'mobile' / 'unknown'). The richer Device entity
                      (OS, OS version, app version, model, hostname,
                      multi-device-per-session linkage) is the next
                      step — see the design doc; for now the column
                      shows the coarse class. Empty for PENDING tasks. */}
                  <TableCell sx={tableHeadCellSx}>Device</TableCell>
                  <TableCell sx={tableHeadCellSx}>Created</TableCell>
                  <TableCell sx={tableHeadCellSx}>Retries</TableCell>
                  <TableCell sx={tableHeadCellSx}>Failure</TableCell>
                  <TableCell sx={tableHeadCellSx}>Job</TableCell>
                  <TableCell sx={tableHeadCellSx}>Progress</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map(t => (
                  <EdgeRow
                    key={t.id}
                    task={t}
                    client={client}
                    linkedJob={
                      t.extractionJobId
                        ? linkedJobsById.get(t.extractionJobId) ?? null
                        : undefined
                    }
                    profileName={t.profileId ? profileNameById.get(t.profileId) ?? null : null}
                    selected={selectedIds.has(t.id)}
                    onToggleSelected={toggleSelected}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <RetryAllFailedDialog
        open={retryAllOpen}
        onClose={() => setRetryAllOpen(false)}
        loading={retryAllState.loading}
        onSubmit={async ({taskTypes: selectedTypes, sinceMs}) => {
          try {
            const res = await retryAllFailedExtractions({
              variables: {
                taskTypes: selectedTypes.length === 0 ? null : selectedTypes,
                sinceMs,
                limit: 500,
              },
            });
            const r = res.data?.retryAllFailedEdgeTaskExtractions;
            if (!r) {
              notify({severity: 'error', message: 'Retry returned no data.'});
              return;
            }
            const breakdown = r.perTypeCounts
              .map(p => `${p.taskType}: ${p.enqueued}/${p.scanned}`)
              .join(', ');
            notify({
              severity: r.enqueued > 0 ? 'success' : 'info',
              message:
                r.enqueued > 0
                  ? `Enqueued ${r.enqueued} retry job${r.enqueued === 1 ? '' : 's'} (scanned ${r.scanned}${r.enqueueFailures > 0 ? `, ${r.enqueueFailures} enqueue failure(s)` : ''}). ${breakdown}`
                  : `No FAILED extractions found in the selected scope.`,
              durationMs: 10000,
            });
            setRetryAllOpen(false);
            refetch();
          } catch (err) {
            notify({
              severity: 'error',
              message: `Bulk retry failed: ${err instanceof Error ? err.message : String(err)}`,
            });
          }
        }}
      />
    </Box>
  );
}

// ----------------------------------------------------------------------------
// Retry-all-failed dialog
//
// Lets the admin pick:
//   - HOW FAR BACK to look (preset windows + a custom days input + "all time")
//   - WHICH TASK TYPES to consider (multi-select; empty selection ⇒ all)
//
// Defaults to 1 week + all extractable types so the most common case
// ("rescue everything that broke recently") is one click → one
// confirm. The mutation itself is bounded (limit=500/type) and idempotent —
// re-running pulls anything still in FAILED, ignores anything already
// re-enqueued.
// ----------------------------------------------------------------------------
interface RetryAllFailedDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (args: {taskTypes: EdgeQueueTaskType[]; sinceMs: number | null}) => void;
}

const RANGE_PRESETS: Array<{label: string; days: number | null}> = [
  {label: 'Last 24 hours', days: 1},
  {label: 'Last week', days: 7},
  {label: 'Last 30 days', days: 30},
  {label: 'Last 90 days', days: 90},
  {label: 'All time', days: null},
];

const RETRYABLE_TYPES: EdgeQueueTaskType[] = [
  EdgeQueueTaskType.QueryFeed,
  EdgeQueueTaskType.QueryHistory,
  EdgeQueueTaskType.QueryRelated,
];

function RetryAllFailedDialog({open, onClose, loading, onSubmit}: RetryAllFailedDialogProps) {
  // Default = "Last week" (matches the most common "what just broke?"
  // usage). Stored as days-from-now so the conversion to sinceMs is
  // computed at submit time (not when the dialog mounted).
  const [rangeDays, setRangeDays] = useState<number | null>(7);
  const [customDays, setCustomDays] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<EdgeQueueTaskType[]>([]);

  const isCustom = rangeDays === -1;
  const effectiveDays =
    rangeDays === null
      ? null
      : isCustom
        ? Number(customDays) > 0
          ? Number(customDays)
          : null
        : rangeDays;

  const sinceMs =
    effectiveDays === null
      ? null
      : Date.now() - effectiveDays * 24 * 60 * 60 * 1000;

  const handleSubmit = () => {
    onSubmit({taskTypes: selectedTypes, sinceMs});
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>Retry all failed extractions</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{fontSize: 13, mb: 2}}>
          Enqueues an EXTRACT_EDGE_TASK background job for every task in the FAILED extraction
          state matching the scope below. Each job re-runs the latest parser fallback chain;
          extractions that succeed will move out of FAILED. Bounded at 500 per task type.
        </DialogContentText>

        <Typography sx={{fontSize: 12, fontWeight: 600, mt: 1, mb: 0.5}}>
          How far back?
        </Typography>
        <RadioGroup
          value={isCustom ? 'custom' : rangeDays === null ? 'all' : String(rangeDays)}
          onChange={e => {
            const v = e.target.value;
            if (v === 'all') setRangeDays(null);
            else if (v === 'custom') setRangeDays(-1);
            else setRangeDays(Number(v));
          }}
        >
          {RANGE_PRESETS.map(p => (
            <FormControlLabel
              key={p.label}
              value={p.days === null ? 'all' : String(p.days)}
              control={<Radio size="small" />}
              label={<Typography sx={{fontSize: 13}}>{p.label}</Typography>}
            />
          ))}
          <FormControlLabel
            value="custom"
            control={<Radio size="small" />}
            label={
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography sx={{fontSize: 13}}>Custom (last</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={customDays}
                  onChange={e => {
                    setCustomDays(e.target.value);
                    setRangeDays(-1);
                  }}
                  onFocus={() => setRangeDays(-1)}
                  inputProps={{min: 1, style: {width: 60, fontSize: 13, padding: '4px 8px'}}}
                />
                <Typography sx={{fontSize: 13}}>days)</Typography>
              </Box>
            }
          />
        </RadioGroup>

        <Typography sx={{fontSize: 12, fontWeight: 600, mt: 2, mb: 0.5}}>
          Task types
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            multiple
            displayEmpty
            value={selectedTypes}
            onChange={e => {
              const v = e.target.value;
              setSelectedTypes(typeof v === 'string' ? (v.split(',') as EdgeQueueTaskType[]) : (v as EdgeQueueTaskType[]));
            }}
            input={<OutlinedInput />}
            renderValue={sel => {
              const arr = sel as EdgeQueueTaskType[];
              if (arr.length === 0) return <em style={{color: '#94A3B8'}}>All types</em>;
              return arr.join(', ');
            }}
          >
            {RETRYABLE_TYPES.map(t => (
              <MenuItem key={t} value={t} sx={{fontFamily: 'monospace'}}>
                <Checkbox checked={selectedTypes.indexOf(t) > -1} size="small" />
                <ListItemText primary={t} primaryTypographyProps={{fontFamily: 'monospace', fontSize: 13}} />
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{fontSize: 11, color: '#64748B', mt: 0.5}}>
            Leave empty to sweep every extractable type (Feed, History, Related).
          </Typography>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button size="small" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || (isCustom && (Number(customDays) <= 0 || customDays.trim() === ''))}
        >
          {loading ? 'Enqueuing…' : 'Retry all'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------- Session check heartbeat --------------------------------------

interface SessionCheckReturn {
  activeSessions: number;
  tasksCreated: number;
  tasksSkipped: number;
}

/**
 * Self-fetching variant of `SessionCheckHeartbeat` for callers that
 * don't already have a list of BullMQ jobs to pass in (e.g. the
 * Admin Command Center). Polls the BG queue every 5s for recent
 * Active/Completed/Failed jobs and renders the same panel.
 */
export function SessionCheckHeartbeatStandalone({client}: {client: ApolloClient<NormalizedCacheObject>}) {
  const {data} = useListBullmqJobs(client, {
    states: [BullmqJobState.Completed, BullmqJobState.Active, BullmqJobState.Failed],
    limit: 50,
  });
  return <SessionCheckHeartbeat jobs={data?.listBullmqJobs || []} />;
}

function SessionCheckHeartbeat({jobs}: {jobs: BullmqJobEntry[]}) {
  const sessionJobs = useMemo(
    () => jobs.filter(j => j.name === AnalysisQueueMessageType.ControlHeartbeatSessions),
    [jobs],
  );
  const completed = useMemo(
    () =>
      sessionJobs
        .filter(j => j.state === BullmqJobState.Completed && j.returnValueJson)
        .slice(0, 20),
    [sessionJobs],
  );
  const latest = completed[0];
  const latestResult: SessionCheckReturn | null = useMemo(() => {
    if (!latest?.returnValueJson) return null;
    try {
      return JSON.parse(latest.returnValueJson) as SessionCheckReturn;
    } catch {
      return null;
    }
  }, [latest]);

  const running = sessionJobs.find(j => j.state === BullmqJobState.Active);
  const failed = sessionJobs.filter(j => j.state === BullmqJobState.Failed).length;

  // 60-second cron cadence — show how long since the last completed tick so
  // the user can tell whether the scheduler is alive.
  const msSinceLast = latest?.finishedAt
    ? Date.now() - new Date(latest.finishedAt).getTime()
    : null;
  const healthy = msSinceLast != null && msSinceLast < 3 * 60_000;
  const heartbeatColor = running
    ? pcolors.blue[500]
    : healthy
      ? pcolors.green[500]
      : pcolors.amber[500];

  return (
    <Paper sx={{...cardSx, p: 2, mb: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: heartbeatColor,
              animation: running ? 'pulse 1.2s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {opacity: 1, transform: 'scale(1)'},
                '50%': {opacity: 0.6, transform: 'scale(1.25)'},
              },
            }}
          />
          <Typography sx={{fontSize: 14, fontWeight: 600, color: pcolors.textPrimary}}>
            Session checker
          </Typography>
          <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>every 60s · BullMQ repeatable job</Typography>
        </Box>
        <Box sx={{flex: 1}} />
        <Typography sx={{fontSize: 11, color: pcolors.textMuted}}>
          {latest?.finishedAt ? `last tick ${formatRelative(latest.finishedAt)}` : 'no ticks yet'}
        </Typography>
      </Box>

      <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2}}>
        <Stat
          label="Active sessions (last 5 min)"
          value={latestResult?.activeSessions ?? '—'}
        />
        <Stat
          label="Tasks created (last tick)"
          value={latestResult?.tasksCreated ?? '—'}
          emphasis={latestResult && latestResult.tasksCreated > 0 ? pcolors.green[700] : undefined}
        />
        <Stat label="Tasks skipped (last tick)" value={latestResult?.tasksSkipped ?? '—'} />
        <Stat
          label="Recent ticks · failed"
          value={`${completed.length} · ${failed}`}
          emphasis={failed > 0 ? pcolors.red[700] : undefined}
        />
      </Box>

      {/* Sparkline: bars = tasksCreated on each of the last N ticks */}
      {completed.length > 0 && (
        <Box sx={{mt: 2}}>
          <Typography sx={{...sectionLabelSx, mb: 0.75}}>Tasks created per tick — last {completed.length}</Typography>
          <Box sx={{display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 40}}>
            {completed
              .slice()
              .reverse()
              .map(j => {
                let v = 0;
                try {
                  v = (JSON.parse(j.returnValueJson!) as SessionCheckReturn).tasksCreated || 0;
                } catch {
                  /* ignore */
                }
                const height = Math.max(2, Math.min(40, v * 4));
                return (
                  <Tooltip key={j.id} title={`${v} tasks · ${new Date(j.finishedAt || j.createdAt).toLocaleTimeString()}`}>
                    <Box
                      sx={{
                        width: 14,
                        height,
                        bgcolor: v > 0 ? pcolors.blue[500] : pcolors.border,
                        borderRadius: 0.5,
                      }}
                    />
                  </Tooltip>
                );
              })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function Stat({label, value, emphasis}: {label: string; value: React.ReactNode; emphasis?: string}) {
  return (
    <Box>
      <Typography sx={sectionLabelSx}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          mt: 0.25,
          fontVariantNumeric: 'tabular-nums',
          color: emphasis || pcolors.textPrimary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// ------------- Background row ------------------------------------------------

interface JobProgress {
  stage?: string;
  current?: number;
  total?: number;
  fractionComplete?: number;
  message?: string;
  costUSD?: number;
  startedAt?: string;
  updatedAt?: string;
}

function parseProgress(raw: string | null | undefined): JobProgress | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function BackgroundRow({job, onCancel}: {job: BullmqJobEntry; onCancel: (jobId: string) => void}) {
  const [open, setOpen] = useState(false);
  const {bg, color} = bullmqJobStateColors(job.state);
  const progress = parseProgress(job.progressJson);
  // Terminal job states are authoritative — if BullMQ flipped the job to
  // COMPLETED/FAILED, ignore whatever stage string the handler left behind
  // so the UI doesn't misleadingly say "aggregating" next to a completed job.
  const terminal =
    job.state === BullmqJobState.Completed || job.state === BullmqJobState.Failed;
  const cancelledByAdmin =
    job.state === BullmqJobState.Failed &&
    !!job.failedReason &&
    job.failedReason.toLowerCase().startsWith('cancelled by admin');
  // 'cancelling' is an intermediate state where the resolver set the signal
  // but the worker hasn't exited yet — render in amber so the admin sees the
  // click was registered even though the job is still ACTIVE in BullMQ.
  const cancelling = !terminal && progress?.stage === 'cancelling';
  const displayedStage = terminal ? null : progress?.stage;
  const pct = terminal
    ? 100
    : progress?.fractionComplete != null
      ? Math.round(progress.fractionComplete * 100)
      : null;
  const cancellable =
    job.state === BullmqJobState.Waiting ||
    job.state === BullmqJobState.Active ||
    job.state === BullmqJobState.Delayed;

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  // When the user lands on `/admin/operations/tasks?jobId=<id>` (the app uses
  // HashRouter so the real URL is `/#/admin/operations/tasks?jobId=<id>`), this
  // row scrolls itself into view + flashes. The effect re-runs whenever
  // the hash changes so back/forward keeps it accurate.
  const [targeted, setTargeted] = React.useState(false);
  const rowRef = React.useRef<HTMLTableRowElement | null>(null);

  React.useEffect(() => {
    const check = () => {
      // HashRouter stuffs the real URL (path + query) into window.location.hash,
      // e.g. `#/admin/operations/tasks?jobId=1485`. URLSearchParams of the substring
      // after the first `?` gets us `jobId` without another router hook.
      const raw = typeof window !== 'undefined' ? window.location.hash : '';
      const qIdx = raw.indexOf('?');
      if (qIdx === -1) {
        setTargeted(false);
        return;
      }
      const params = new URLSearchParams(raw.slice(qIdx + 1));
      const match = params.get('jobId') === String(job.id);
      setTargeted(match);
      if (match && rowRef.current) {
        rowRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
    };
    check();
    window.addEventListener('hashchange', check);
    return () => window.removeEventListener('hashchange', check);
  }, [job.id]);

  return (
    <>
      <TableRow
        id={`job-${job.id}`}
        ref={rowRef}
        hover
        sx={{
          '& > *': {borderBottom: `1px solid ${pcolors.borderLight}`},
          '&:hover': {bgcolor: pcolors.rowHover},
          ...(targeted
            ? {
                animation: 'goodshowhqJobFlash 1.8s ease-out 0s 2',
                '@keyframes goodshowhqJobFlash': {
                  '0%': {backgroundColor: pcolors.amber[50]},
                  '100%': {backgroundColor: 'transparent'},
                },
              }
            : {}),
        }}
      >
        <TableCell sx={{...tableBodyCellSx, width: 36}}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 12, color: pcolors.textSecondary}}>
          #{job.id}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 11, color: pcolors.textPrimary}}>
          {job.name}
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          <StatePill bg={bg} color={color}>{humanizeBullmqJobState(job.state)}</StatePill>
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, minWidth: 180}}>
          {progress || terminal ? (
            <Box sx={{width: '100%'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    color: cancelledByAdmin
                      ? pcolors.amber[700]
                      : cancelling
                        ? pcolors.amber[700]
                        : pcolors.textSecondary,
                  }}
                >
                  {cancelledByAdmin
                    ? 'cancelled'
                    : cancelling
                      ? 'cancelling…'
                      : terminal
                        ? job.state === BullmqJobState.Completed
                          ? 'done'
                          : 'failed'
                        : displayedStage || 'running'}
                </Typography>
                <Box sx={{flex: 1}} />
                {pct != null && !cancelling && (
                  <Typography sx={{fontSize: 11, color: pcolors.textSecondary, fontVariantNumeric: 'tabular-nums'}}>
                    {pct}%
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant={cancelling || pct == null ? 'indeterminate' : 'determinate'}
                value={pct ?? 0}
                sx={{
                  mt: 0.5,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: pcolors.borderLight,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: cancelledByAdmin
                      ? pcolors.amber[500]
                      : cancelling
                        ? pcolors.amber[500]
                        : terminal
                          ? job.state === BullmqJobState.Completed
                            ? pcolors.green[500]
                            : pcolors.red[500]
                          : pcolors.blue[500],
                  },
                }}
              />
              {progress && !terminal && !cancelling && progress.current != null && progress.total != null && (
                <Typography sx={{fontSize: 10, color: pcolors.textMuted, mt: 0.25, fontVariantNumeric: 'tabular-nums'}}>
                  {progress.current}/{progress.total}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>—</Typography>
          )}
        </TableCell>
        <TableCell
          sx={{
            ...tableBodyCellSx,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            color: progress?.costUSD != null && progress.costUSD > 0 ? pcolors.textPrimary : pcolors.textMuted,
          }}
        >
          {progress?.costUSD != null && progress.costUSD > 0
            ? `$${progress.costUSD.toFixed(4)}`
            : '—'}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary}}>
          <Tooltip title={new Date(job.createdAt).toLocaleString()}>
            <span>{formatRelative(job.createdAt)}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary, fontVariantNumeric: 'tabular-nums'}}>
          {job.attempts}
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, color: pcolors.red[700], maxWidth: 280}}>
          <Typography noWrap sx={{fontSize: 12}}>{job.failedReason || '—'}</Typography>
        </TableCell>
        <TableCell sx={{...tableBodyCellSx, width: 40, textAlign: 'center'}}>
          {cancellable && (
            <Tooltip title={
              job.state === BullmqJobState.Active
                ? 'Cancel this running job (will also prevent retries — in-flight LLM call finishes but no further work)'
                : 'Remove this queued job'
            }>
              <IconButton size="small" onClick={() => onCancel(job.id)} sx={{color: pcolors.red[600]}}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{p: 0, borderBottom: 'none'}} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{p: 2, bgcolor: pcolors.bg, display: 'grid', gap: 2}}>
              {job.failedReason && (
                <Box>
                  <Typography sx={sectionLabelSx}>Error</Typography>
                  <Box sx={{mt: 1, ...cardSx, p: 1.5, maxHeight: 400, overflow: 'auto', bgcolor: pcolors.red[50], borderColor: pcolors.red[100]}}>
                    <pre style={{margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: pcolors.red[700]}}>
                      {job.failedReason}
                    </pre>
                  </Box>
                </Box>
              )}
              {job.logs && job.logs.length > 0 && (
                <Box>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography sx={sectionLabelSx}>Logs</Typography>
                    <Typography sx={{fontSize: 10, color: pcolors.textMuted, fontVariantNumeric: 'tabular-nums'}}>
                      ({job.logs.length} line{job.logs.length === 1 ? '' : 's'})
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(job.logs!.join('\n'))}>
                      <ContentCopyIcon sx={{fontSize: 14}} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      ...cardSx,
                      p: 1.5,
                      maxHeight: 480,
                      overflow: 'auto',
                      bgcolor: '#0B1020',
                      borderColor: '#1E2842',
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontSize: 11,
                        lineHeight: 1.5,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        color: '#D1D5DB',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {job.logs.join('\n')}
                    </pre>
                  </Box>
                </Box>
              )}
              {progress && (
                <Box>
                  <Typography sx={sectionLabelSx}>Progress detail</Typography>
                  <Box sx={{mt: 1, ...cardSx, p: 1.5}}>
                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2}}>
                      <Box>
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase'}}>Stage</Typography>
                        <Typography sx={{fontSize: 13, mt: 0.25}}>{progress.stage || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase'}}>Current</Typography>
                        <Typography sx={{fontSize: 13, mt: 0.25, fontVariantNumeric: 'tabular-nums'}}>{progress.current ?? '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase'}}>Total</Typography>
                        <Typography sx={{fontSize: 13, mt: 0.25, fontVariantNumeric: 'tabular-nums'}}>{progress.total ?? '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase'}}>Updated</Typography>
                        <Typography sx={{fontSize: 13, mt: 0.25}}>
                          {progress.updatedAt ? formatRelative(progress.updatedAt) : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    {progress.message && (
                      <Typography sx={{fontSize: 12, mt: 1, color: pcolors.textPrimary}}>{progress.message}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              <JsonBlobViewer
                label="Payload"
                json={(() => {
                  // Pretty-print so the inline <pre> renders the same
                  // indented shape it did before — but only when the
                  // JSON is small enough to bother. For oversized blobs
                  // the file we download is the source string verbatim
                  // (still valid JSON, just not pretty).
                  try {
                    return JSON.stringify(JSON.parse(job.dataJson), null, 2);
                  } catch {
                    return job.dataJson;
                  }
                })()}
                filename={`bullmq-job-${job.id}-payload.json`}
              />
              {job.returnValueJson && (
                <JsonBlobViewer
                  label="Return value"
                  json={(() => {
                    try {
                      return JSON.stringify(JSON.parse(job.returnValueJson), null, 2);
                    } catch {
                      return job.returnValueJson;
                    }
                  })()}
                  filename={`bullmq-job-${job.id}-return.json`}
                />
              )}
              <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2}}>
                <Box>
                  <Typography sx={sectionLabelSx}>Created</Typography>
                  <Typography sx={{fontSize: 13, mt: 0.5}}>{new Date(job.createdAt).toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography sx={sectionLabelSx}>Processed</Typography>
                  <Typography sx={{fontSize: 13, mt: 0.5}}>
                    {job.processedAt ? new Date(job.processedAt).toLocaleString() : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={sectionLabelSx}>Finished</Typography>
                  <Typography sx={{fontSize: 13, mt: 0.5}}>
                    {job.finishedAt ? new Date(job.finishedAt).toLocaleString() : '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ------------- Background panel ----------------------------------------------

const ALL_JOB_STATES: BullmqJobState[] = [
  BullmqJobState.Waiting,
  BullmqJobState.Active,
  BullmqJobState.Delayed,
  BullmqJobState.Completed,
  BullmqJobState.Failed,
  BullmqJobState.Paused,
];

/**
 * Worker fleet panel — connected workers + per-worker active job
 * counts. Polls every 5s. Shows:
 *   - Total workers + total active jobs (header)
 *   - Per-worker row with name, addr, age, idle, active/concurrency
 *     utilization bar
 *   - Concurrency + rate-limit settings the API process believes
 *     are in effect (best-effort — workers run in a separate Node
 *     process; the values are taken from the API's env)
 *
 * Surfaces the WAITING-pile diagnosis at a glance: if the bars are
 * all at concurrency cap, the queue is concurrency-bound; if they're
 * empty but jobs are WAITING, the workers aren't picking up.
 */
function WorkersPanel({client}: {client: Props['client']}) {
  const {data, loading} = useGetBullmqWorkers(client);
  const snap = data?.getBullmqWorkers;
  const total = snap?.totalWorkers ?? 0;
  const active = snap?.totalActiveJobs ?? 0;
  // Per-queue concurrency lookup. With the 8-tier split each Worker
  // is bound to one queue with that tier's concurrency budget — the
  // headline `concurrencyPerWorker` is the SUM, but per-row
  // utilization needs the row's tier-specific number.
  const concurrencyByQueue = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of snap?.concurrencyByQueue ?? []) m.set(c.queueName, c.concurrency);
    return m;
  }, [snap]);
  // "Fleet capacity" = sum across queues of (workers-on-queue ×
  // tier-concurrency). With one worker process running, this equals
  // sum of per-tier concurrencies.
  const fleetCapacity = useMemo(() => {
    let cap = 0;
    for (const w of snap?.workers ?? []) {
      cap += concurrencyByQueue.get(w.queueName) ?? 0;
    }
    return cap;
  }, [snap, concurrencyByQueue]);

  return (
    <Paper sx={{...cardSx, p: 2, mb: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: 14, fontWeight: 600, color: pcolors.textPrimary}}>
          Workers
        </Typography>
        <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>
          {total} worker connection{total === 1 ? '' : 's'} across {concurrencyByQueue.size} queue{concurrencyByQueue.size === 1 ? '' : 's'} ·
          {' '}{active} active / {fleetCapacity} fleet capacity
          {snap && ` · rate ${snap.rateLimitMax}/${Math.round(snap.rateLimitDurationMs / 1000)}s`}
        </Typography>
        <Box sx={{flex: 1}} />
        {loading && !data && <CircularProgress size={14} />}
      </Box>
      {total === 0 ? (
        <Alert severity="warning" variant="outlined" sx={{fontSize: 12, mt: 1}}>
          No workers connected. WAITING jobs will sit until at least one worker
          process is running and connected to Redis.
        </Alert>
      ) : (
        <TableContainer sx={{...cardSx, mt: 1}}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={tableHeadCellSx}>Queue</TableCell>
                <TableCell sx={tableHeadCellSx}>Worker</TableCell>
                <TableCell sx={tableHeadCellSx}>Address</TableCell>
                <TableCell sx={tableHeadCellSx}>Age</TableCell>
                <TableCell sx={tableHeadCellSx}>Idle</TableCell>
                <TableCell sx={{...tableHeadCellSx, minWidth: 160}}>Active jobs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {snap?.workers.map((w, idx) => {
                const queueConcurrency = concurrencyByQueue.get(w.queueName) ?? 0;
                const utilization =
                  queueConcurrency > 0
                    ? Math.min(100, (w.activeJobs / queueConcurrency) * 100)
                    : 0;
                const stale = (w.idleSeconds ?? 0) > 90;
                // Strip the goodshowhq- prefix in the display column —
                // the wire name is busy and the prefix never varies.
                const tierLabel = w.queueName.startsWith('goodshowhq-')
                  ? w.queueName.slice('goodshowhq-'.length)
                  : w.queueName;
                return (
                  <TableRow key={`${w.queueName}:${w.name}:${idx}`}>
                    <TableCell sx={{...tableBodyCellSx, fontWeight: 600, fontSize: 12}}>
                      {tierLabel}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, fontFamily: 'monospace', fontSize: 11, color: pcolors.textSecondary}}>
                      {w.name || '(unnamed)'}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary, fontSize: 11}}>
                      {w.addr ?? '—'}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, color: pcolors.textSecondary, fontSize: 11}}>
                      {w.ageSeconds != null ? `${w.ageSeconds}s` : '—'}
                    </TableCell>
                    <TableCell sx={{
                      ...tableBodyCellSx,
                      color: stale ? pcolors.amber[700] : pcolors.textSecondary,
                      fontSize: 11,
                    }}>
                      {w.idleSeconds != null ? `${w.idleSeconds}s` : '—'}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 140}}>
                        <Typography sx={{fontSize: 11, fontVariantNumeric: 'tabular-nums', minWidth: 50}}>
                          {w.activeJobs}/{queueConcurrency}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={utilization}
                          color={utilization >= 100 ? 'error' : utilization >= 75 ? 'warning' : 'primary'}
                          sx={{flex: 1, height: 6, borderRadius: 3}}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function BackgroundPanel({client}: {client: Props['client']}) {
  // Default to every state so the user sees their jobs (including
  // freshly completed ones like a reeval) without having to tick a
  // filter first. The filter chips below let them narrow down.
  const [states, setStates] = useState<BullmqJobState[]>([...ALL_JOB_STATES]);
  // Job-type filter. Empty array = "all types" (default). Populated
  // from whatever distinct names show up in the current data window
  // — keeps the dropdown short instead of listing every enum value
  // including ones that never run in this deployment.
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  // Derive admin claim so the Bull Board entry only renders for
  // users who can actually use it. The server gates the route too
  // (see `routes/admin_queue.ts:requireAdmin`), but hiding the
  // button avoids the surprise click-into-401-page UX.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    return onAuthStateChanged(auth, async user => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const t = await user.getIdTokenResult();
        setIsAdmin(t.claims.role === 'admin');
      } catch {
        setIsAdmin(false);
      }
    });
  }, []);

  /**
   * Open Bull Board with a fresh Firebase ID token.
   *
   * Bull Board lives at `/admin/queues/` (note trailing slash —
   * Firebase Hosting rewrites both `/admin/queues` and `/admin/queues/**`
   * to the api function, but the Express mount point inside the
   * function only serves clean paths). The server's `requireAdmin`
   * accepts either a `__session` cookie, a `Bearer` header, or a
   * `?token=` query string. We fetch the current user's id token
   * via firebase auth and append it as `?token=` so the new tab
   * authenticates immediately — no copy-paste-token login dance.
   */
  const handleOpenBullBoard = async (): Promise<void> => {
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken(/*forceRefresh*/ false) : null;
      const url = token
        ? `/admin/queues/?token=${encodeURIComponent(token)}`
        : '/admin/queues/';
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[AdminTasks] open Bull Board failed:', err);
      // Fall back to a plain navigation; the server will render
      // the paste-token login page.
      window.open('/admin/queues/', '_blank', 'noopener,noreferrer');
    }
  };
  const [limit, setLimit] = useState(100);
  // Multi-select queue scope. All 9 queues selected by default;
  // admins deselect tiers to silence the noisy ones. The full list
  // also defines display order in the dropdown — control first
  // (cron / janitorial), then the SLA tiers, then the legacy drain.
  const ALL_QUEUE_NAMES = useMemo(
    () => [
      'control',
      'ingest',
      'process',
      'fetch',
      'triage',
      'scoring',
      'aggregate',
      'recommend',
      'tasks',
    ],
    [],
  );
  const [queueScope, setQueueScope] = useState<string[]>(ALL_QUEUE_NAMES);

  // Job types hidden from the default view because a richer surface
  // exists (heartbeat panel above, Edge Tasks tab) or because they're
  // high-volume noise (channel fan-out). See the long comment further
  // down for full rationale per type. Declared up here so the
  // useListBullmqJobs call below can ship them to the server as
  // `excludeNames` — server-side exclusion is what lets the limit
  // apply to user-visible rows. The frontend filter that consumes
  // this same set below is now a belt-and-suspenders safeguard for
  // any rows that slip through (e.g. a legacy job name not in the
  // hidden set).
  const HIDDEN_BY_DEFAULT = useMemo(
    () =>
      new Set<string>([
        AnalysisQueueMessageType.ControlHeartbeatSessions,
        AnalysisQueueMessageType.IngestAckFeed,
        AnalysisQueueMessageType.IngestAckHistory,
        AnalysisQueueMessageType.IngestAckRelated,
        AnalysisQueueMessageType.IngestAckTranscript,
        AnalysisQueueMessageType.EvaluateChannelActions,
      ]),
    [],
  );

  const {data, loading, refetch} = useListBullmqJobs(client, {
    states,
    limit,
    // All-selected ⇔ omit the filter (equivalent results, but the
    // backend takes the cheaper "fan across everything" path
    // without scanning the included-set per queue).
    queues: queueScope.length === ALL_QUEUE_NAMES.length ? null : queueScope,
    // Two-way server-side name filter:
    //   - Admin selected types via the Type picker → includeNames
    //     (whitelist; `limit` applies to matching rows only).
    //   - No explicit selection → excludeNames the noisy defaults
    //     (heartbeat, ingest-acks, channel fan-out).
    // The backend treats includeNames as winning over excludeNames,
    // so an opted-in type is never blocked by a default-hidden one.
    includeNames: typeFilter.length > 0 ? typeFilter : null,
    excludeNames: typeFilter.length === 0 ? Array.from(HIDDEN_BY_DEFAULT) : null,
  });
  const [cancelJob] = useCancelBullmqJob(client);
  const [removeJobs, removeState] = useRemoveBullmqJobs(client);
  const confirm = useConfirm();
  const notify = useNotify();

  const handleCancelOne = async (jobId: string) => {
    const ok = await confirm({
      title: 'Cancel this job?',
      message:
        'Waiting/delayed jobs are removed immediately. Active jobs finish any in-flight LLM call then exit — BullMQ can\'t preempt async code mid-call.',
      confirmLabel: 'Cancel job',
      destructive: true,
    });
    if (!ok) return;
    const res = await cancelJob({variables: {jobId}});
    notify({
      severity: res.data?.cancelBullmqJob ? 'success' : 'warning',
      message: res.data?.cancelBullmqJob ? `Job ${jobId} cancelled.` : `Job ${jobId} could not be cancelled (already gone?).`,
    });
    refetch();
  };

  const handleClearFailed = async () => {
    const ok = await confirm({
      title: 'Clear all FAILED jobs?',
      message: 'All failed jobs in BullMQ history will be removed. Running, waiting, and completed jobs are untouched.',
      confirmLabel: 'Clear failed',
      destructive: true,
    });
    if (!ok) return;
    const res = await removeJobs({variables: {states: [BullmqJobState.Failed]}});
    notify({
      severity: 'success',
      message: `Removed ${res.data?.removeBullmqJobs ?? 0} failed job${res.data?.removeBullmqJobs === 1 ? '' : 's'}.`,
    });
    refetch();
  };

  // SessionCheck heartbeat moved to AdminCommandCenter — admins
  // working in the command center already have the profile/session
  // context that the heartbeat is most informative against. The BG
  // jobs view now leads with the worker fleet panel (which supplies
  // the right context for waiting-pile diagnosis here).
  const allJobs = data?.listBullmqJobs || [];

  // HIDDEN_BY_DEFAULT is declared above the useListBullmqJobs call
  // so it can flow through as `excludeNames`. Rationale per type:
  //   - CONTROL_HEARTBEAT_SESSIONS — the heartbeat panel above
  //     already surfaces this once-per-minute pulse; raw rows would
  //     dominate the table.
  //   - INGEST_ACK_*           — the Edge Tasks tab shows the task
  //     lifecycle, parser version, parse stats, and final state
  //     (COMPLETED / FAILED with errors). The BG-jobs view of an ack
  //     is just BullMQ-machinery noise on the success path.
  //   - EVALUATE_CHANNEL_ACTIONS — leaf fan-out from one
  //     EVALUATE_PROFILE_ACTIONS produces N author jobs (one per
  //     channel the profile has interacted with), easily 50+ rows
  //     per profile refresh. The PROFILE-level row is the useful
  //     aggregate; admins debugging a single author can opt-in via
  //     the Type filter (selected types appear dimmed in the
  //     dropdown so admins can see at a glance what's hidden by
  //     default).
  //
  // Admins debugging a stuck queue can opt-in to any of these via
  // the Type filter dropdown — only the default view hides them.
  //
  // Note we don't hide INGEST_ACK_GENERIC (it's the rare-fires
  // fallback — when it does fire, you want to see it) or
  // INGEST_REPARSE_* (admin-triggered, low-volume, the per-job log
  // lines are the whole point of having them in the BG-jobs view).

  // typeFilter + HIDDEN_BY_DEFAULT are applied server-side via
  // includeNames/excludeNames on listBullmqJobs, so allJobs already
  // arrives filtered. No client-side filter pass needed — the
  // table renders the returned set directly.
  const jobs = allJobs;

  // Job-type list for the picker. Sourced from the full
  // `AnalysisQueueMessageType` enum (minus RunSessionHeartbeat which
  // has its own dedicated panel above and is never useful to look at
  // here) so admins can pre-filter for a type even when no jobs of
  // that type are currently in the data window — e.g. wait until the
  // next SCORE_LIBRARY lands without re-opening the picker each
  // refresh.
  //
  // EDGE_ACK_* types ARE included so admins can opt-in to seeing
  // them — they're just hidden from the default view because the
  // Edge Tasks tab is a richer surface for the same data.
  //
  // Counts shown next to each option come from the current data
  // window across ALL jobs (not just the visible filtered set), so
  // an admin sees that EDGE_ACK_HISTORY exists in the window even
  // before opting in to viewing it.
  const typeCountsInWindow = useMemo(() => {
    const m: Record<string, number> = {};
    for (const j of allJobs) {
      if (j.name !== AnalysisQueueMessageType.ControlHeartbeatSessions) {
        m[j.name] = (m[j.name] ?? 0) + 1;
      }
    }
    return m;
  }, [allJobs]);

  const availableTypes = useMemo(() => {
    // Union of (a) the current enum and (b) any legacy names actually
    // present in the data window. BullMQ stores `name` on the job
    // record at enqueue time, so jobs that landed before an enum
    // rename keep their old name (e.g. `GENERATE_CORRECTIVE_ACTION_FOR_AUTHOR`
    // → `GENERATE_CORRECTIVE_ACTIONS_AUTHOR`). Without the union,
    // those jobs render in the table with their old name but the
    // dropdown only lists the new name — selecting it filters them
    // all out. Including the actual `j.name`s lets admins pick the
    // legacy label and surface them for the migration tool.
    const set = new Set<string>(Object.values(AnalysisQueueMessageType) as string[]);
    for (const t of Object.keys(typeCountsInWindow)) set.add(t);
    set.delete(AnalysisQueueMessageType.ControlHeartbeatSessions);
    // Group by namespace family (the part before the first '_') so
    // SCORE_*, EDGE_*, EVAL_*, GENERATE_*, PRUNE_* cluster together,
    // then alphabetical within each group. Legacy names (no
    // recognized prefix) sort to the bottom under a single bucket so
    // they don't fragment the families. Sorting on count was
    // confusing — counts shift between refreshes and the family
    // grouping is the predictable mental model.
    const FAMILY_ORDER = ['CONTROL', 'INGEST', 'PROCESS', 'FETCH', 'TRIAGE', 'SCORING', 'AGGREGATE', 'RECOMMEND'];
    const familyRank = (t: string): number => {
      const prefix = t.split('_')[0];
      const idx = FAMILY_ORDER.indexOf(prefix);
      return idx === -1 ? FAMILY_ORDER.length : idx;
    };
    return Array.from(set).sort((a, b) => {
      const fa = familyRank(a);
      const fb = familyRank(b);
      if (fa !== fb) return fa - fb;
      return a.localeCompare(b);
    });
  }, [typeCountsInWindow]);

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const j of jobs) m[j.state] = (m[j.state] ?? 0) + 1;
    return m;
  }, [jobs]);

  // Sum cost across the currently shown jobs so admins can see at a glance
  // how much the current queue view has spent (handy for spotting runaway
  // evals / bulk rescores without clicking into each one).
  const totalCostUSD = useMemo(() => {
    let sum = 0;
    for (const j of jobs) {
      const p = parseProgress(j.progressJson);
      if (p?.costUSD) sum += p.costUSD;
    }
    return sum;
  }, [jobs]);

  return (
    <Box>
      <TabHeader
        title="Background jobs"
        subtitle="Work dispatched to the BullMQ worker — video processing, scoring, corrective actions, and evals."
        actions={
          <>
            {isAdmin && (
              <Tooltip title="Open Bull Board (BullMQ admin UI). Uses your current Firebase ID token to authenticate the new tab — no paste-token step.">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenBullBoard}
                >
                  Bull Board
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleClearFailed}
              disabled={removeState.loading}
            >
              Clear failed
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      <WorkersPanel client={client} />

      <Paper sx={{...cardSx, p: 2, mb: 2}}>
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          {/* Queue scope — multi-select. All 9 queues selected by
              default; deselect tiers to silence them. Empty selection
              wraps to "all" so the panel never shows an empty result
              by mistake (would look identical to "no jobs running"). */}
          <FormControl size="small" sx={{minWidth: 220}}>
            <InputLabel>Queues</InputLabel>
            <Select
              multiple
              value={queueScope}
              input={<OutlinedInput label="Queues" />}
              onChange={e => {
                const v = e.target.value as string[];
                // Empty selection → snap back to all.
                setQueueScope(v.length === 0 ? ALL_QUEUE_NAMES : v);
              }}
              renderValue={sel => {
                const arr = sel as string[];
                if (arr.length === ALL_QUEUE_NAMES.length) return 'All queues';
                if (arr.length === 0) return '(none)';
                return arr.join(', ');
              }}
            >
              {ALL_QUEUE_NAMES.map(q => {
                const tierLabel = q === 'tasks' ? 'legacy (drain)' : q;
                return (
                  <MenuItem key={q} value={q} sx={{fontSize: 13}}>
                    <Checkbox size="small" checked={queueScope.indexOf(q) > -1} />
                    <ListItemText primary={tierLabel} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 260}}>
            <InputLabel>States</InputLabel>
            <Select
              multiple
              value={states}
              input={<OutlinedInput label="States" />}
              onChange={e =>
                setStates(
                  typeof e.target.value === 'string'
                    ? (e.target.value.split(',') as BullmqJobState[])
                    : (e.target.value as BullmqJobState[])
                )
              }
              renderValue={sel =>
                (sel as BullmqJobState[]).length === 0
                  ? 'All states'
                  : (sel as BullmqJobState[]).map(humanizeBullmqJobState).join(', ')
              }
            >
              {ALL_JOB_STATES.map(s => (
                <MenuItem key={s} value={s} sx={{fontSize: 13}}>
                  <Checkbox size="small" checked={states.indexOf(s) > -1} />
                  <ListItemText primary={humanizeBullmqJobState(s)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 240}}>
            <InputLabel>Types</InputLabel>
            <Select
              multiple
              value={typeFilter}
              input={<OutlinedInput label="Types" />}
              onChange={e =>
                setTypeFilter(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : (e.target.value as string[])
                )
              }
              renderValue={sel =>
                (sel as string[]).length === 0 ? 'All types' : (sel as string[]).join(', ')
              }
            >
              {availableTypes.map(t => {
                const count = typeCountsInWindow[t] ?? 0;
                const hiddenByDefault = HIDDEN_BY_DEFAULT.has(t);
                const secondaryParts: string[] = [];
                secondaryParts.push(count > 0 ? `${count} in view` : 'none in view');
                if (hiddenByDefault) {
                  secondaryParts.push('hidden by default · see Edge Tasks tab');
                }
                return (
                  <MenuItem key={t} value={t} sx={{fontSize: 13, fontFamily: 'monospace'}}>
                    <Checkbox size="small" checked={typeFilter.indexOf(t) > -1} />
                    <ListItemText
                      primary={t}
                      primaryTypographyProps={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        // Dim hidden-by-default rows so admins can see at a
                        // glance "this type exists but isn't shown unless I
                        // pick it explicitly."
                        color: hiddenByDefault ? pcolors.textMuted : pcolors.textPrimary,
                      }}
                      secondary={secondaryParts.join(' · ')}
                      secondaryTypographyProps={{fontSize: 10}}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 100}}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit.toString()} label="Limit" onChange={e => setLimit(Number(e.target.value))}>
              {[25, 50, 100, 250, 500, 1000, 2500, 5000].map(n => (
                <MenuItem key={n} value={n} sx={{fontSize: 13}}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{flex: 1, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap'}}>
            {totalCostUSD > 0 && (
              <Tooltip title="Sum of reported LLM cost across the jobs shown">
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    bgcolor: pcolors.green[50],
                    color: pcolors.green[700],
                    fontSize: 12,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${totalCostUSD.toFixed(4)}
                  <Typography component="span" sx={{fontSize: 10, fontWeight: 500, opacity: 0.75}}>
                    total
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {ALL_JOB_STATES.filter(s => counts[s] > 0).map(s => {
              const {bg, color} = bullmqJobStateColors(s);
              return (
                <Box key={s} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <StatePill bg={bg} color={color}>{humanizeBullmqJobState(s)}</StatePill>
                  <Typography sx={{fontSize: 12, color: pcolors.textSecondary, fontVariantNumeric: 'tabular-nums'}}>
                    {counts[s]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{...cardSx, overflow: 'hidden'}}>
        {loading && !data ? (
          <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}><CircularProgress size={24} /></Box>
        ) : jobs.length === 0 ? (
          <Box sx={{p: 5, textAlign: 'center'}}>
            <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
              No background jobs match the selected filters. Is Redis running and the worker started?
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx} />
                  <TableCell sx={tableHeadCellSx}>Job</TableCell>
                  <TableCell sx={tableHeadCellSx}>Type</TableCell>
                  <TableCell sx={tableHeadCellSx}>State</TableCell>
                  <TableCell sx={{...tableHeadCellSx, minWidth: 180}}>Progress</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Cost</TableCell>
                  <TableCell sx={tableHeadCellSx}>Created</TableCell>
                  <TableCell sx={tableHeadCellSx}>Attempts</TableCell>
                  <TableCell sx={tableHeadCellSx}>Error</TableCell>
                  <TableCell sx={tableHeadCellSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map(j => <BackgroundRow key={j.id} job={j} onCancel={handleCancelOne} />)}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

// ------------- Root ----------------------------------------------------------

type AdminTasksTab = 'edge' | 'background';

const VALID_TABS: AdminTasksTab[] = ['edge', 'background'];

export default function AdminTasks({client}: Props) {
  usePageTitle('Admin – Tasks');
  // Tab is URL-driven so refresh / share links preserve it. Route
  // is `/admin/operations/tasks/:tab`; the tab-less form renders the
  // default and pushes a redirect so the URL becomes canonical — that
  // way the browser back button doesn't trap the user on the tabless
  // URL.
  const navigate = useNavigate();
  const params = useParams<{tab?: string}>();
  const urlTab = params.tab as AdminTasksTab | undefined;
  const tab: AdminTasksTab =
    urlTab && VALID_TABS.includes(urlTab) ? urlTab : 'background';

  React.useEffect(() => {
    if (urlTab !== tab) {
      navigate(`/admin/operations/tasks/${tab}`, {replace: true});
    }
  }, [urlTab, tab, navigate]);

  const handleTabChange = (_: unknown, v: AdminTasksTab) => {
    if (v === tab) return;
    navigate(`/admin/operations/tasks/${v}`);
  };

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 2}}>
        <Typography sx={pageTitleSx}>Tasks &amp; jobs</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          Edge tasks run on the desktop app. Background jobs run on the BullMQ worker.
        </Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{
          mb: 2,
          borderBottom: `1px solid ${pcolors.border}`,
          '& .MuiTab-root': {minHeight: 40, fontSize: 13},
        }}
      >
        <Tab value="background" label="Background jobs" />
        <Tab value="edge" label="Edge tasks" />
      </Tabs>
      {tab === 'edge' ? <EdgePanel client={client} /> : <BackgroundPanel client={client} />}
    </Box>
  );
}
